// One job on the board, as a list row: who, where, what it pays, and (for
// remote roles in a family we measure) where that pay sits against the
// typical remote range. Links open the job in a new tab, like the old cards,
// so the list keeps its scroll position.
import { useState } from "react";
import type { Job } from "@/types";
import { getRelativeTimeString } from "@/lib/dateUtils";
import { cleanLocation } from "../utils/cleanLocation";
import { buildJobPath } from "@/utils/jobSlug";
import { payFamilyFor, type PayFamily } from "@/hooks/useHomeLive";
import { employerName, EMPLOYER_UNNAMED, UnnamedEmployerMark } from "@/lib/employer";

const TONES = ["#e4f222", "#dbe7ff", "#e3f6ec", "#f3e8ff", "#ffe9d6"];

// One colour per company, so the same employer looks the same on every row.
const toneFor = (company: string) => {
  let h = 0;
  for (const c of company || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return TONES[h % TONES.length];
};

const initials = (company: string) => {
  const words = (company || "").replace(/[^a-zA-Z ]/g, " ").split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "A") + (words[1]?.[0] ?? "")).toUpperCase();
};

// "$145K-175K" or "$52K" (annual USD, as display_salary writes it). Hourly,
// monthly and non-USD strings return null: no band beats a wrong band.
export function annualRange(text: string | null | undefined): [number, number] | null {
  const m = /^\$(\d+(?:\.\d+)?)K(?:-(\d+(?:\.\d+)?)K)?$/.exec((text || "").trim());
  if (!m) return null;
  const lo = parseFloat(m[1]) * 1000;
  const hi = (m[2] ? parseFloat(m[2]) : parseFloat(m[1])) * 1000;
  return lo >= 20000 && hi >= lo ? [lo, hi] : null;
}

const LO = 40000, HI = 260000;
const pct = (v: number) => Math.max(0, Math.min(100, ((v - LO) / (HI - LO)) * 100));
const k = (v: number) => `$${Math.round(v / 1000)}k`;

type Props = { job: Job; families?: Record<string, PayFamily>; onOpen?: () => void };

export default function BoardJobRow({ job, families, onOpen }: Props) {
  const [logoFailed, setLogoFailed] = useState(false);
  const featured = !!(job.isFeatured || job.is_featured);
  const remote = !!(job.is_remote || job.isRemote);
  const loc = cleanLocation(job.display_location || job.location) || (remote ? "Remote" : "");
  const pay = job.display_salary || job.salary || "";
  const range = remote ? annualRange(pay) : null;
  const famName = range ? payFamilyFor(job.title) : null;
  const fam = famName && families ? families[famName] : undefined;
  const band = range && fam && fam.n >= 30 ? { range, fam, famName: famName as string } : null;
  const above = !!band && (band.range[0] + band.range[1]) / 2 > band.fam.p75;
  const posted = getRelativeTimeString(job.post_date);
  const hours = job.hours && job.hours !== "Full-Time" ? job.hours : null;
  const employer = employerName(job.company);

  return (
    <article className={`bj-row${featured ? " is-featured" : ""}`}>
      {!employer ? (
        <div className="bj-row__logo bj-row__logo--unnamed" aria-hidden="true"><UnnamedEmployerMark size={20} /></div>
      ) : job.logo && !logoFailed ? (
        <div className="bj-row__logo bj-row__logo--img" aria-hidden="true">
          <img src={job.logo} alt="" loading="lazy" onError={() => setLogoFailed(true)} />
        </div>
      ) : (
        <div className="bj-row__logo" style={{ background: toneFor(job.company) }} aria-hidden="true">{initials(job.company)}</div>
      )}
      <div className="bj-row__main">
        <h3 className="bj-row__title">
          <a href={buildJobPath(job)} target="_blank" rel="noopener noreferrer" onClick={onOpen}>
            {job.titleHighlight ? <span dangerouslySetInnerHTML={{ __html: job.titleHighlight }} /> : job.title}
          </a>
        </h3>
        <p className="bj-row__meta">
          {employer ?? <span className="bj-row__unnamed">{EMPLOYER_UNNAMED}</span>}
          {loc ? ` · ${loc}` : ""}
        </p>
        <div className="bj-row__tags">
          {featured && <span className="bj-tag bj-tag--featured">Featured</span>}
          {remote && <span className="bj-tag">Remote</span>}
          {hours && <span className="bj-tag">{hours}</span>}
          {above && <span className="bj-tag bj-tag--above">Pays above the typical range</span>}
        </div>
      </div>
      <div className="bj-row__pay">
        {pay && <p className="bj-row__amount">{pay}</p>}
        {band && (
          <>
            <div className="bj-band" role="img" aria-label={`Typical remote ${band.famName} pay: ${k(band.fam.p25)} to ${k(band.fam.p75)}`}>
              <span className="bj-band__fam" style={{ left: `${pct(band.fam.p25)}%`, width: `${pct(band.fam.p75) - pct(band.fam.p25)}%` }} />
              <span className="bj-band__post" style={{ left: `${pct(band.range[0])}%`, width: `${Math.max(1.5, pct(band.range[1]) - pct(band.range[0]))}%` }} />
            </div>
            <p className="bj-row__note">Typical remote {band.famName}: {k(band.fam.p25)} to {k(band.fam.p75)}</p>
          </>
        )}
        <p className="bj-row__when">{posted}</p>
      </div>
    </article>
  );
}
